'use client';

import React, { useEffect, useState } from 'react';
// --- UPDATED: Import and use the custom useUser hook ---
import { useUser } from '../../context/UserContext'; 
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';
import AlertsList from '../../components/AlertsList.js';
import {
  ChartBarIcon,
  CloudArrowUpIcon,
  FolderIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

// --- Imports for Chart.js (unchanged) ---
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import {
  ArrowUpFromLine,
  ChartNoAxesColumnIncreasing,
  Folders,
  Percent,
  Cog,
} from 'lucide-react';

// --- Register Chart.js components (unchanged) ---
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler
);

export default function Dashboard() {
  // --- UPDATED: Consume the context via the useUser() hook ---
  const { user, organization, subscriptionInfo, loading: userLoading } = useUser();
  const isMemberInOrganization = !!organization; // <-- ADD THIS LINE


  const [metrics, setMetrics] = useState({ totalMeasurements: 0, totalProjects: 0, dailyMeasurements: [] });
  const [teamMemberActivity, setTeamMemberActivity] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Quick action cards definitions (unchanged)
  const actionCards = [
    { title: 'Upload Measurements', description: 'Upload new measurement data', icon: <ArrowUpFromLine className="h-6 w-6 text-gray-600" />, link: '/upload' },
    { title: 'View Measurements', description: 'Filter and sort all measurements', icon: <Percent className="h-6 w-6 text-gray-600" />, link: '/measurements' },
    { title: 'View Projects', description: 'View your projects', icon: <Folders className="h-6 w-6 text-gray-600" />, link: '/projects' },
    { title: 'Settings', description: 'Manage your account settings', icon: <Cog className="h-6 w-6 text-gray-600" />, link: '/settings' },
  ];

  useEffect(() => {
    document.title = 'Dashboard | TransTech Connect';
    return () => { document.title = 'TransTech Connect'; };
  }, []);

  // --- Data fetching logic (unchanged, but now depends on values from useUser) ---
  useEffect(() => {
    // Exit early if the necessary context data isn't available from useUser hook
    if (!user || !organization) {
      if (!userLoading) { // Only stop loading if the user context is also done loading
          setMetricsLoading(false);
      }
      return;
    }

    const fetchMetrics = async () => {
      setMetricsLoading(true);
      try {
        const orgId = organization; 

        const { count: totalMeasurements } = await supabase.from('measurements_with_details').select('*', { count: 'exact', head: true }).eq('organization_id', orgId);
        const { count: totalProjects } = await supabase.from('projects').select('*', { count: 'exact', head: true }).eq('organization_id', orgId);

        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

        const { data: recentMeasurements } = await supabase
          .from('measurements_with_details')
          .select('id, profile_id, operator_name, measurement_date')
          .eq('organization_id', orgId)
          .gte('measurement_date', fourteenDaysAgo.toISOString());

        const dailyMeasurements = [];
        const today = new Date();
        for (let i = 13; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          const count = recentMeasurements?.filter((m) => m.measurement_date === dateStr).length || 0;
          dailyMeasurements.push({ date: dateStr, count, displayDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
        }

        const memberCounts = new Map();
        const memberNames = new Map();
        recentMeasurements.forEach(m => {
            memberCounts.set(m.profile_id, (memberCounts.get(m.profile_id) || 0) + 1);
            if (!memberNames.has(m.profile_id)) {
                memberNames.set(m.profile_id, m.operator_name || 'Unknown User');
            }
        });
        
        const activityData = Array.from(memberCounts.entries())
            .map(([userId, count]) => ({ name: memberNames.get(userId), count }))
            .sort((a, b) => b.count - a.count);

        setTeamMemberActivity(activityData);
        setMetrics({ totalMeasurements, totalProjects, dailyMeasurements });

      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [user, organization, userLoading]);

  // --- Chart.js Configurations (unchanged) ---
  const lineChartOptions = {
    responsive: true, maintainAspectRatio: false, tension: 0.4,
    plugins: { legend: { display: false }, tooltip: { enabled: true, backgroundColor: '#fff', titleColor: '#333', bodyColor: '#ec9315', titleFont: { weight: 'bold' }, bodyFont: { size: 14 }, padding: 12, borderColor: '#ec9315', borderWidth: 1, boxPadding: 4, callbacks: { label: (context) => `${context.parsed.y} Measurements` } } },
    scales: { x: { grid: { color: '#f3f4f6' }, border: { display: false }, ticks: { color: '#6b7280' } }, y: { beginAtZero: true, grid: { color: '#f3f4f6' }, border: { display: false }, ticks: { color: '#6b7280', precision: 0 } } },
    interaction: { intersect: false, mode: 'index' },
    elements: { point: { radius: 0, hoverRadius: 6, hitRadius: 10, hoverBackgroundColor: '#fff', hoverBorderWidth: 2 } }
  };

  const lineChartData = {
    labels: metrics.dailyMeasurements.map(d => d.displayDate),
    datasets: [{
      label: 'Daily Measurements', data: metrics.dailyMeasurements.map(d => d.count), fill: true, borderColor: '#ec9315',
      backgroundColor: (context) => {
        const ctx = context.chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, 'rgba(236, 147, 21, 0.2)');
        gradient.addColorStop(1, 'rgba(236, 147, 21, 0)');
        return gradient;
      },
      pointBorderColor: '#ec9315',
    }],
  };
  
  // --- Bar Chart Options (Unchanged) ---
const barChartOptions = {
    indexAxis: 'y', // This makes it a horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false
        },
        tooltip: {
            enabled: true,
            backgroundColor: '#fff',
            titleColor: '#333',
            bodyColor: '#ec9315',
            titleFont: { weight: 'bold' },
            bodyFont: { size: 14 },
            padding: 12,
            borderColor: '#ec9315',
            borderWidth: 1,
            boxPadding: 4,
            callbacks: {
                label: (context) => `${context.parsed.x} Measurements`
            }
        }
    },
    scales: {
        x: {
            beginAtZero: true,
            grid: {
                display: false
            },
            border: {
                display: false
            },
            ticks: {
                color: '#6b7280',
                precision: 0
            }
        },
        y: {
            grid: {
                display: false
            },
            border: {
                display: false
            },
            ticks: {
                color: '#6b7280'
            }
        }
    },
};


// --- Bar Chart Data (with updated barPercentage) ---
const barChartData = {
    labels: teamMemberActivity.map(d => d.name),
    datasets: [{
        label: 'Measurements',
        data: teamMemberActivity.map(d => d.count),
        backgroundColor: '#ec9315',
        borderColor: '#d38312',
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.1, // <-- This value was changed from 0.6 to make bars skinnier
    }],
};

  // Combine loading states: the dashboard is loading if the user data OR the metrics data is loading.
  if (userLoading || metricsLoading) {
    return <Layout><div className="p-8 text-center text-gray-500">Loading Dashboard...</div></Layout>;
  }

  return (
    <Layout noPadding={true}>
      <div className="mx-auto p-2">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <h2 className="text-base font-semibold text-gray-900">Dashboard</h2>
            <p className="text-sm text-gray-600">Welcome back! Here's an overview of your organization's activity.</p>
          </div>

          <div className="pb-6 px-6 space-y-8">
            <div className="pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {actionCards.map((card) => {
                  // Determine if this specific card should be disabled
                  const isDisabled = (card.title === 'Upload Measurements' || card.title === 'View Projects') && !isMemberInOrganization;
                  
                  return (
                    <a
                      key={card.title}
                      href={isDisabled ? undefined : card.link} // Prevent navigation by removing href
                      onClick={(e) => { if (isDisabled) e.preventDefault(); }} // Prevent click action as a fallback
                      aria-disabled={isDisabled} // Improve accessibility
                      className={`flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200 group transition-shadow duration-200 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:bg-gray-50'}`}
                    >
                      <div className="flex-shrink-0 p-3 bg-gray-100 rounded-full border border-gray-200 shadow-sm">{card.icon}</div>
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-gray-900 group-hover:text-gray-700">{card.title}</h4>
                        <p className="text-sm text-gray-500">{card.description}</p>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
  {/* Left Column: Line Chart */}
  <div className="lg:col-span-2">
    <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200 h-full">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Overview (Last 14 Days)</h3>
        <div className="h-80">
          {metrics.totalMeasurements === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ChartBarIcon className="h-12 w-12 text-gray-300 mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No measurement data yet</h4>
              <p className="text-sm text-gray-500 max-w-md">Scan your PQI380+ with our mobile app to see your organization's activity visualized here.</p>
              <p className="text-sm text-gray-400 mt-2 italic">Note: You must be a member of a Team to upload .pqidat files.</p>
            </div>
          ) : ( <Line options={lineChartOptions} data={lineChartData} /> )}
        </div>
      </div>
    </div>
  </div>

  {/* Right Column: Total Card + Stretchy Bar Chart */}
  <div className="lg:col-span-1 flex flex-col gap-8">
    {/* Top Card: Total Measurements */}
    <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <ChartBarIcon className="h-6 w-6 text-gray-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600">Total Measurements</p>
          <p className="text-3xl font-semibold text-gray-900">{metrics.totalMeasurements?.toLocaleString() || 0}</p>
        </div>
      </div>
    </div>

    {/* Bottom Card: Team Performance (this card will stretch) */}
    <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200 flex-1 flex flex-col">
      <div className="p-6 flex flex-col h-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance</h3>
        <p className="text-sm text-gray-500 -mt-3 mb-4">Last 14 Days</p>
        {/* This container grows to fill the remaining card space */}
        <div className="relative flex-grow min-h-0">
          {teamMemberActivity.length === 0 || teamMemberActivity.every(m => m.count === 0) ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ChartNoAxesColumnIncreasing className="h-10 w-10 text-gray-300 mb-3" />
              <h4 className="text-base font-medium text-gray-900 mb-1">No recent team activity</h4>
              <p className="text-xs text-gray-500">Activity will appear here.</p>
            </div>
          ) : ( <Bar options={barChartOptions} data={barChartData} /> )}
        </div>
      </div>
    </div>
  </div>
</div>
            <AlertsList />
          </div>
        </div>
      </div>
    </Layout>
  );
}